/** Context selectors for an officer-entered real movement, never a provisional plug. */
import type { RecordTablePayload } from './recordTypes'
import type { RecordContext } from './navigation/context'
import type { ContextWorkPayload } from './workbench/contextWork'

export const MISSING_MOVEMENT = 'officer_reconciling_movement'
export interface ManualMovementSeed {
  fundId: string
  kind: 'position' | 'instrument'
  id: string
}
export interface ManualMovementChoice {
  kind: ManualMovementSeed['kind']
  id: string
  label: string
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function manualMovementHref(
  fundId: string,
  target: Pick<ManualMovementSeed, 'kind' | 'id'>
) {
  const params = new URLSearchParams({ mode: 'missing-movement', fund: fundId })
  params.set(`${target.kind}_id`, target.id)
  return `/v2/manual?${params}`
}

export function manualMovementSeed(params: URLSearchParams): ManualMovementSeed | null {
  if (params.get('mode') !== 'missing-movement') return null
  const fundId = params.get('fund')
  const position = params.get('position_id')
  const instrument = params.get('instrument_id')
  if (
    !fundId ||
    ['mode', 'fund', 'position_id', 'instrument_id'].some((key) => params.getAll(key).length > 1) ||
    !UUID.test(fundId) ||
    !!position === !!instrument ||
    !UUID.test(position || instrument || '')
  )
    throw new Error(
      'Choose one recorded position or instrument in its fund before adding a movement.'
    )
  return { fundId, kind: position ? 'position' : 'instrument', id: (position || instrument)! }
}

export function manualMovementChoices(
  record: RecordTablePayload,
  fundId: string
): ManualMovementChoice[] {
  if (record.fund_id !== fundId || record.as_of) return []
  return [
    ...record.positions
      .filter((row) => row.state === 'recorded' || row.state === 'evidenced')
      .map((row) => ({
        kind: 'position' as const,
        id: row.id,
        label:
          [
            row.holder_name,
            row.instrument_name,
            row.issuer_name,
            row.position_role === 'single' ? null : row.position_role,
          ]
            .filter(Boolean)
            .join(' · ') || 'Position',
      })),
    ...record.instruments
      .filter((row) => row.state === 'recorded' || row.state === 'evidenced')
      .map((row) => ({
        kind: 'instrument' as const,
        id: row.id,
        label: [row.name, row.issuer_name].filter(Boolean).join(' · ') || 'Instrument',
      })),
  ]
}

export function manualMovementBookLinks(
  context: RecordContext,
  result: { id: string; groupId: string; fundId: string }
): { label: string; href: string }[] {
  if (!context.fund_ids.includes(result.fundId)) return []
  const work = context.work as unknown as ContextWorkPayload
  const proposals = new Set(
    (work.proposals ?? [])
      .filter(
        (proposal) =>
          proposal.group_id === result.groupId &&
          proposal.proposal_type === 'create_trade' &&
          proposal.status === 'pending' &&
          proposal.target?.kind === 'trade' &&
          proposal.target.id === result.id
      )
      .map((proposal) => proposal.id)
  )
  return work.cards
    .filter(
      (card) =>
        card.fund_ids.includes(result.fundId) &&
        card.book_proposal_ids.some((id) => proposals.has(id))
    )
    .map((card) => {
      const params = new URLSearchParams({
        scope: 'fund',
        fund: result.fundId,
        queue: 'book',
        card: card.id,
        group: result.groupId,
      })
      return { label: card.label, href: `/v2/workbench?${params}` }
    })
}
